<!-- Phase 1 — Task 3C: admin list of submitted host applications (with approve / reject actions) -->
<template>
  <div class="row">
    <div class="col-md-12">
      <card card-body-classes="table-full-width">
        <div slot="header" style="display:flex; justify-content:space-between; align-items:center;">
          <h4 class="card-title" style="margin:0;">Host Applications</h4>
          <div>
            <label style="margin-right: 8px;">Status:</label>
            <select v-model="statusFilter" @change="fetch" class="form-control status-filter" style="display:inline-block; width:auto;">
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="">All</option>
            </select>
          </div>
        </div>
        <div>
          <div v-if="loading">Loading…</div>
          <div v-else-if="!applications.length" style="padding:1rem;">
            No host applications {{ statusFilter ? 'with status "' + statusFilter + '"' : '' }} yet.
          </div>
          <el-table v-else :data="applications" :highlight-current-row="false">
            <el-table-column label="Submitted" :min-width="140">
              <template slot-scope="props">
                {{ formatDate(props.row.created_at) }}
              </template>
            </el-table-column>
            <el-table-column label="Form details" :min-width="220">
              <template slot-scope="props">
                <div style="font-weight:600;">{{ props.row.name }}</div>
                <div style="font-size:0.85em; color:#9aa;">{{ props.row.email }}</div>
                <div style="font-size:0.85em; color:#9aa;">{{ props.row.phone_number }}</div>
              </template>
            </el-table-column>
            <el-table-column label="Submitted by (app user)" :min-width="220">
              <template slot-scope="props">
                <div v-if="props.row.submitted_by" style="font-weight:600;">
                  {{ (props.row.submitted_by.first_name || '') + ' ' + (props.row.submitted_by.last_name || '') }}
                </div>
                <div v-if="props.row.submitted_by" style="font-size:0.85em; color:#9aa;">
                  {{ props.row.submitted_by.email }}
                </div>
                <div v-if="props.row.submitted_by" style="font-size:0.85em; color:#9aa;">
                  {{ props.row.submitted_by.phone_number }}
                </div>
                <div v-else style="color:#c66; font-size:0.85em;">
                  ⚠ legacy / missing user_id — cannot approve
                </div>
              </template>
            </el-table-column>
            <el-table-column label="Event" :min-width="220">
              <template slot-scope="props">
                <div v-if="props.row.event" style="font-weight:600;">{{ props.row.event.event_name }}</div>
                <div v-if="props.row.event" style="font-size:0.85em; color:#9aa;">
                  {{ props.row.event.date }} {{ props.row.event.start_time }}–{{ props.row.event.end_time }}
                </div>
                <div v-if="props.row.event" style="font-size:0.85em; color:#9aa;">{{ props.row.event.address }}</div>
                <div v-else style="color:#9aa;">—</div>
              </template>
            </el-table-column>
            <el-table-column label="Frequency" prop="hosting_frequency" :min-width="110" />
            <el-table-column label="Event Types" :min-width="160">
              <template slot-scope="props">
                {{props.row.event_types}}
              </template>
            </el-table-column>
            <el-table-column label="Status" :min-width="100">
              <template slot-scope="props">
                <span :style="statusStyle(props.row.status)">{{ props.row.status }}</span>
              </template>
            </el-table-column>
            <el-table-column label="Actions" :min-width="200">
              <template slot-scope="props">
                <div v-if="props.row.status === 'pending'">
                  <base-button type="success" size="sm" :disabled="acting === props.row._id"
                    @click="approve(props.row)" style="margin-right:4px;">Approve</base-button>
                  <base-button type="danger" size="sm" :disabled="acting === props.row._id"
                    @click="reject(props.row)">Reject</base-button>
                </div>
                <span v-else style="opacity:0.6;">—</span>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </card>
    </div>
  </div>
</template>

<script>
import { Table, TableColumn } from "element-ui";

export default {
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
  },
  data() {
    return {
      applications: [],
      loading: true,
      acting: null,
      statusFilter: "pending",
    };
  },
  methods: {
    formatDate(d) {
      try { return new Date(d).toLocaleString(); } catch (e) { return d; }
    },
    statusStyle(status) {
      const map = {
        pending:  { color: "#e0a800" },
        approved: { color: "#28a745" },
        rejected: { color: "#dc3545" },
      };
      return Object.assign({ textTransform: "capitalize", fontWeight: 600 }, map[status] || {});
    },
    getApiMessage(res, fallback) {
      const responseData =
        (res && res.apidata) ||
        (res && res.error && res.error.response && res.error.response.data);

      return (
        (responseData && responseData.Message) ||
        (responseData && responseData.message) ||
        fallback
      );
    },
    async fetch() {
      this.loading = true;
      try {
        const url = this.statusFilter
          ? `host/host-application/list?status=${encodeURIComponent(this.statusFilter)}`
          : "host/host-application/list";
        const res = await this.$apiService.getCall(url);
        this.applications = (res && res.apidata && res.apidata.Data) || [];
      } catch (e) {
        console.log("host-application/list failed:", e);
      } finally {
        this.loading = false;
      }
    },
    async approve(row) {
      if (!confirm(`Approve ${row.name} as host for "${row.event && row.event.event_name}"?`)) return;
      this.acting = row._id;
      try {
        const res = await this.$apiService.postCall(`host/host-application/${row._id}/approve`, {});
        if (res && res.apidata && res.apidata.Code === "P00001") {
          this.$apiService.notify("Approved — applicant added to event organizers");
          await this.fetch();
        } else {
          this.$apiService.notify(this.getApiMessage(res, "Approve failed"));
        }
      } catch (e) {
        this.$apiService.notify(this.getApiMessage({ error: e }, "Approve failed"));
      } finally {
        this.acting = null;
      }
    },
    async reject(row) {
      if (!confirm(`Reject ${row.name}'s application?`)) return;
      this.acting = row._id;
      try {
        const res = await this.$apiService.postCall(`host/host-application/${row._id}/reject`, {});
        if (res && res.apidata && res.apidata.Code === "P00001") {
          this.$apiService.notify("Rejected");
          await this.fetch();
        } else {
          this.$apiService.notify(this.getApiMessage(res, "Reject failed"));
        }
      } catch (e) {
        this.$apiService.notify(this.getApiMessage({ error: e }, "Reject failed"));
      } finally {
        this.acting = null;
      }
    },
  },
  mounted() {
    this.fetch();
  },
};
</script>

<style scoped>
/* Override all the bright-white backgrounds element-ui can apply on top of the dark admin:
   striped rows, current-row, hover-row. Keep rows transparent, show only a subtle hover. */
::v-deep .el-table,
::v-deep .el-table tr,
::v-deep .el-table__row,
::v-deep .el-table__row--striped,
::v-deep .el-table__row.current-row,
::v-deep .el-table__body tr {
  background-color: transparent !important;
}
::v-deep .el-table tr > td,
::v-deep .el-table__row--striped > td {
  background-color: transparent !important;
}
::v-deep .el-table tr:hover > td,
::v-deep .el-table__body tr.hover-row > td {
  background-color: rgba(255, 255, 255, 0.04) !important;
}

.status-filter {
  background-color: #ffffff;
  color: #000000;
}

.status-filter option {
  background-color: #ffffff;
  color: #000000;
}
</style>
